import { useContext, useRef } from "react";
import { UserContext } from "@src/App";
import { PhotoProviderData } from "./Provider";

export function usePhotoActions() {
  // provider data
  const {
    partner,
    getOfficialPhoto,
    getReviewPhoto,
    setOfficialPhotoData,
    setReviewPhotoData,
    officialPhotoData,
    reviewPhotoData,
    setOfficialPhotoTotal,
    setReviewPhotoTotal,
    getFolderPhoto,
    selectedFolder,
    setSelectedFolder,
  } = useContext(PhotoProviderData).data;

  // user
  const user = useContext(UserContext);

  // 공식 이미지 패치 사이즈
  const PHOTO_FETCH_SIZE = 21; // grid 3x7

  // 리뷰 이미지 패치 사이즈
  const PHOTO_FETCH_SIZE_FOR_REVIEW = 21; // grid 3x7

  // 공식 이미지 패치 시작 인덱스
  const photoFetchStartIndex = useRef(0);

  // 리뷰 이미지 패치 시작 인덱스
  const photoFetchStartIndexForReview = useRef(0);

  // 이미지 패치 중 여부
  const photoIsFetching = useRef(false);

  // 이미지 패치 종료 여부 (추가 호출 필요 없음 플래그)
  const photoFetchEnd = useRef(false);

  /**
   *
   * @param callType 호출 타입 (init: 초기 호출, add: 추가 호출)
   * @param param 별도 호출 파라미터
   * @returns 추가된 이미지 갯수
   */
  const getOfficialPhotoByType = async (callType: "init" | "add", param: any) => {
    if (!partner?.uuid || !user?.me?.uuid) return 0;
    const params = param // 별도 파라미터 있을 경우 사용
      ? param
      : {
          // 기본대로 사용할 경우 파라미터 전달 없이 사용
          fetchSize: PHOTO_FETCH_SIZE,
          fetchStart: photoFetchStartIndex.current,
          partnerUuid: partner.uuid,
          uuid: user.me.uuid,
        };

    return getOfficialPhoto(params, partner.uuid).then((res) => {
      const data = res?.data?.item;
      if (data) {
        switch (callType) {
          case "init":
            setOfficialPhotoData(data);
            setOfficialPhotoTotal(res.data.total);
            break;
          case "add":
            if (!officialPhotoData || !res.data.item) return;
            setOfficialPhotoData([...officialPhotoData, ...res.data.item]);
            break;
        }
      }
      // 시작 이미지 인덱스 + 현재 호출된 이미지 갯수가 전체 이미지 갯수보다 작을 경우 추가 호출할 수 있게 data.length 리턴
      // 0으로 리턴되면 호출 종료처리됨
      return photoFetchStartIndexForReview.current + data.length < res.data.total ? data.length : 0;
    });
  };

  /**
   *
   * @param callType 호출 타입 (init: 초기 호출, add: 추가 호출)
   * @param param 별도 호출 파라미터
   * @returns 추가된 이미지 갯수
   * */
  const getReviewPhotoByType = async (callType: "init" | "add", param: any) => {
    if (!partner?.uuid || !user?.me?.uuid) return 0;
    const params = param
      ? param
      : {
          // 기본대로 사용할 경우 파라미터 전달 없이 사용
          fetchSize: PHOTO_FETCH_SIZE_FOR_REVIEW,
          fetchStart: photoFetchStartIndexForReview.current,
          partnerUuid: partner.uuid,
          uuid: user.me.uuid,
        };

    return getReviewPhoto(params, partner.uuid).then((res) => {
      const data = res?.data?.item;
      if (data) {
        switch (callType) {
          case "init":
            setReviewPhotoData(data);
            setReviewPhotoTotal(res.data.total);
            break;
          case "add":
            if (!reviewPhotoData || !res.data.item) return;
            setReviewPhotoData([...reviewPhotoData, ...res.data.item]);
            break;
        }
      }
      // 시작 이미지 인덱스 + 현재 호출된 이미지 갯수가 전체 이미지 갯수보다 작을 경우 추가 호출할 수 있게 data.length 리턴
      // 0으로 리턴되면 호출 종료처리됨
      return photoFetchStartIndexForReview.current + data.length < res.data.total ? data.length : 0;
    });
  };

  /**
   *
   * 해당 함수로 공식 이미지, 리뷰 이미지 호출
   * @param photoType 호출 이미지 타입 (official: 공식 이미지, review: 리뷰 이미지)
   * @param calltype 호출 타입 (init: 초기 호출, add: 추가 호출)
   * @param param 기본방식 대로 사용할 경우 파라미터 전달 없이 사용, 별도 파라미터 전달 필요한 경우 파라미터 넘겨 사용
   */
  const photoListCall = async (
    photoType: "official" | "review",
    calltype: "init" | "add",
    param?: any, // 별도 호출 파라미터
  ) => {
    // 패치 중 플래그 (중복 호출 방지)
    if (photoIsFetching.current) return;
    photoIsFetching.current = true;

    switch (photoType) {
      case "official":
        // 초기화 호출일 경우 시작 인덱스 초기화
        if (calltype === "init") {
          photoFetchEnd.current = false;
          photoFetchStartIndex.current = 0;
        }
        // 공식 이미지 호출
        getOfficialPhotoByType(calltype, param)
          .then((res) => {
            // 0 리턴되면 호출 종료처리
            if (res === 0) {
              photoFetchEnd.current = true;
              return;
            }
            // 시작 인덱스 설정
            if (calltype === "init") photoFetchStartIndex.current = PHOTO_FETCH_SIZE;
            else photoFetchStartIndex.current += PHOTO_FETCH_SIZE;
          })
          .finally(() => {
            // 패치 중 플래그 해제
            photoIsFetching.current = false;
          });
        break;
      case "review":
        // 초기화 호출일 경우 시작 인덱스 초기화
        if (calltype === "init") {
          photoFetchEnd.current = false;
          photoFetchStartIndexForReview.current = 0;
        }
        // 리뷰 이미지 호출
        getReviewPhotoByType(calltype, param)
          .then((res) => {
            // 0 리턴되면 호출 종료처리
            if (res === 0) {
              photoFetchEnd.current = true;
              return;
            }
            // 시작 인덱스 설정
            if (calltype === "init")
              photoFetchStartIndexForReview.current = PHOTO_FETCH_SIZE_FOR_REVIEW;
            else photoFetchStartIndexForReview.current += PHOTO_FETCH_SIZE_FOR_REVIEW;
          })
          .finally(() => {
            // 패치 중 플래그 해제
            photoIsFetching.current = false;
          });
        break;
      default:
        break;
    }
  };

  /**
   *
   * 해당 함수로 폴더 이미지 호출
   * @param callType 호출 타입 (init: 초기 호출, add: 추가 호출) : 현재는 추가 호출 없음
   * @param param 파라미터 전달 (경우가 다른 경우가 많은 거 같아서 우선 파라미터 직접 전달 식으로..)
   */
  const getFolderPhotoByType = async (callType: "init" | "add", param: any) => {
    if (!user?.me?.uuid) return 0;
    if (callType === "init") {
      return getFolderPhoto(param)
        .then((res) => {
          setSelectedFolder({
            ...selectedFolder,
            id: Date.now(),
            photoData: res.data.item,
            total: res.data.total,
          });
        })
        .catch((err) => {
          alert(`폴더 이미지를 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.`);
        });
    }
  };

  return {
    PHOTO_FETCH_SIZE,
    PHOTO_FETCH_SIZE_FOR_REVIEW: PHOTO_FETCH_SIZE_FOR_REVIEW,
    PHTOTO_FETCH_START_INDEX: photoFetchStartIndex.current,
    PHOTO_FETCH_START_INDEX_FOR_REVIEW: photoFetchStartIndexForReview.current,
    PHOTO_IS_FETCHING: photoIsFetching.current,
    PHOTO_IS_FETCH_END: photoFetchEnd.current,
    getFolderPhotoByType,
    photoListCall,
  };
}
